import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { convertCurrencyFormat } from "../helpers";
import { useContext, useEffect, useRef, useState } from "react";
import { useGlobalAlert } from "../contexts/alertContext";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  updateDoc,
  runTransaction,
  getDoc,
  orderBy,
  addDoc,
} from "firebase/firestore";
import { db } from "../firebase-config";
import { LoadingButton } from "@mui/lab";
import { UserContext } from "../contexts/userContext";
import {
  IPaymentMethod,
  IProduct,
  ISale,
  IPromotion,
  IProductToSale,
} from "../interfaces";
import {
  BUSINESS_COLLECTION,
  INGREDIENTS_COLLECTION,
  PAYMENTS_COLLECTION,
  PROMOTIONS_COLLECTION,
  SALES_COLLECTION,
  STOCK_COLLECTION,
} from "../utils/constants";
// import GenericButtons from "./GenericButtons";

const NewOrderModal = ({ editableSale }: { editableSale?: ISale | null }) => {
  const { businessData, userData } = useContext(UserContext);
  const { showAlert } = useGlobalAlert();
  const [item, setItem] = useState({} as IProductToSale);
  const [sale, setSale] = useState({} as ISale);
  const [list, setList] = useState<IProduct[]>([]);
  const [payments, setPayments] = useState<IPaymentMethod[]>([]);
  const [loading, setLoading] = useState(false);
  const [combos, setCombos] = useState<IPromotion[]>([]);
  const [showCombos, setShowCombos] = useState(false);
  const [productQuantities, setProductQuantities] = useState<
    Record<string, number>
  >({});
  const [promoQuantities, setPromoQuantities] = useState<
    Record<string, number>
  >({});
  const [receivedCash, setReceivedCash] = useState<number | string>("");
  const refCampo1 = useRef(null);
  const refCampo2 = useRef(null);
  // const [quantity, setQuantity] = useState<number[]>([]);

  const handleKeyDown = (event: any, nextRef: any) => {
    // 13 es el keyCode para la tecla Enter
    if (event.key === "Enter" || event.keyCode === 13) {
      // Evita el comportamiento por defecto (submit o salto de línea)
      event.preventDefault();

      // Mueve el foco al input del siguiente componente
      if (nextRef.current) {
        // Material UI TextField usa el método .focus() en su elemento input nativo.
        nextRef.current.focus();
      }
    }
  };

  const handleClear = () => {
    setSale({} as ISale);
    setReceivedCash("");
  };

  // const handleChange = (name: string, value: string | number) =>
  //   setItem({ ...item, [name]: value });

  const getProductPriceAndIngredients = (productName: string) => {
    const product = list.find(({ name }: IProduct) => name === productName);
    if (product?.ingredients)
      return {
        price: product.price,
        ingredients: product.ingredients,
        quantity: product.quantity,
      };
    return { price: product?.price || 0 };
  };

  const handleAddItemsToSale = () => {
    const complementaryInfo = getProductPriceAndIngredients(item.name);
    const totalItem = {
      ...item,
      ...complementaryInfo,
    };
    setSale({
      ...sale,
      products:
        sale?.products?.length > 0
          ? sale.products.concat(totalItem)
          : [totalItem],
    });
    setItem({} as IProductToSale);
  };

  // promotions will be selected directly via autocomplete into sale.promotion

  const getList = async () => {
    setLoading(true);
    const listCollectionRef = collection(
      db,
      BUSINESS_COLLECTION,
      businessData.uid,
      STOCK_COLLECTION
    );
    const productsSnap = await getDocs(
      query(
        listCollectionRef,
        where("status", "==", "enabled"),
        orderBy("name")
      )
    );
    setList(
      productsSnap.docs.map(
        (doc) => ({ ...doc.data(), id: doc.id } as IProduct)
      )
    );
    setLoading(false);
  };

  const getCombos = async () => {
    setLoading(true);
    const combosCollectionRef = collection(
      db,
      BUSINESS_COLLECTION,
      businessData.uid,
      PROMOTIONS_COLLECTION
    );
    const combosSnap = await getDocs(combosCollectionRef).finally(() =>
      setLoading(false)
    );
    setCombos(
      combosSnap.docs.map(
        (doc) =>
          ({
            ...doc.data(),
            id: doc.id,
          } as IPromotion)
      )
    );
  };

  const getPayments = async () => {
    setLoading(true);
    const paymentList = localStorage.getItem("paymentMethods") || "[]";

    if (paymentList === "[]") {
      const paymentCollectionRef = collection(
        db,
        BUSINESS_COLLECTION,
        businessData.uid,
        PAYMENTS_COLLECTION
      );
      const { docs } = await getDocs(
        query(paymentCollectionRef, where("status", "==", "enabled"))
      ).finally(() => setLoading(false));

      setPayments(
        docs.map((doc) => ({ ...doc.data(), id: doc.id } as IPaymentMethod))
      );
    } else {
      const payments = JSON.parse(paymentList) as IPaymentMethod[];
      setPayments(payments.filter((pm) => pm.status === "enabled"));
    }
  };

  const getTotalSale = () => {
    let total = 0;

    if (sale?.products && sale?.products?.length > 0)
      total =
        total +
        sale?.products?.reduce(
          (accum: number, product: IProductToSale) =>
            accum + product.price * product.quantityToSale,
          0
        );
    if (sale.promotion && sale?.promotion?.length > 0)
      total =
        total +
        sale.promotion?.reduce(
          (accum: number, item: any) =>
            accum + item.promotion.value * item.quantity,
          0
        );
    return total;
  };

  const removeItemToSale = (nameToRemove: string, type: string) => {
    if (type === "combo") {
      const updatedPromosList = sale.promotion?.filter(
        (promo) => promo.promotion.name !== nameToRemove
      );
      setSale({ ...sale, promotion: updatedPromosList });
    } else {
      const updatedProductsList = sale.products.filter(
        (product) => product.name !== nameToRemove
      );
      setSale({ ...sale, products: updatedProductsList });
    }
    getTotalSale();
  };

  // Función para restar productos e ingredientes en Firestore
  const subtractStockAndIngredients = async () => {
    const allProducts = [];

    if (sale.products && sale.products.length > 0) {
      allProducts.push(...sale.products);
    }

    if (sale.promotion && sale.promotion.length > 0) {
      sale.promotion.forEach((promo) => {
        promo.promotion.applies_to.forEach((prod) => {
          const productInList = list.find((p) => p.id === prod.id);
          if (productInList) {
            allProducts.push({
              ...productInList,
              quantityToSale: prod.quantity_needed * promo.quantity,
            });
          }
        });
      });
    }

    for (const product of allProducts) {
      // Actualizar cantidad de producto en STOCK_COLLECTION
      const productRef = doc(
        db,
        BUSINESS_COLLECTION,
        businessData.uid,
        STOCK_COLLECTION,
        product.id
      );
      const productSnap = await getDoc(productRef);
      if (productSnap.exists()) {
        const currentQty = productSnap.data().quantity || 0;
        await updateDoc(productRef, {
          quantity: currentQty - product.quantityToSale,
        });
      }
      // Si el producto tiene ingredientes, actualizar su cantidad en INGREDIENTS_COLLECTION
      if (product.ingredients && product.ingredients.length > 0) {
        for (const ingredient of product.ingredients) {
          const ingredientRef = doc(
            db,
            BUSINESS_COLLECTION,
            businessData.uid,
            INGREDIENTS_COLLECTION,
            ingredient.id
          );
          const ingredientSnap = await getDoc(ingredientRef);
          if (ingredientSnap.exists()) {
            // Conversion de unidades y calculo consistente de la nueva cantidad
            // Note: ingredient documents store the amount in `stock_quantity`
            const storedQty = ingredientSnap.data().stock_quantity || 0;
            const storedUnit = ingredientSnap.data().unit;
            const neededPerProduct = ingredient.quantity_needed || 0;
            const totalNeeded =
              neededPerProduct * (product.quantityToSale || 0);

            let newQty = storedQty;

            // Si la unidad del ingrediente en la receta es ml pero se guarda en lt
            if (ingredient.unit === "ml" && storedUnit === "lt") {
              const storedML = storedQty * 1000; // convertir a ml
              const updatedML = storedML - totalNeeded; // restar ml necesarios
              newQty = updatedML / 1000; // volver a litros para almacenamiento
            } else if (ingredient.unit === "g" && storedUnit === "kg") {
              const storedG = storedQty * 1000; // convertir a g
              const updatedG = storedG - totalNeeded; // restar gramos necesarios
              newQty = updatedG / 1000; // volver a kg
            } else {
              // mismas unidades (piece, kg/kg, lt/lt, etc.) o casos no mapeados
              newQty = storedQty - totalNeeded;
            }

            // Asegurarse que no quede negativo (opcional: permitir negativo si se desea)
            if (typeof newQty === "number" && newQty < 0) newQty = 0;

            try {
              await runTransaction(db, async (transaction) => {
                const ingSnap = await transaction.get(ingredientRef);
                if (!ingSnap.exists())
                  throw new Error("Ingredient doc missing");
                const current = ingSnap.data().stock_quantity || 0;
                const updated = Math.max(current - totalNeeded, 0);
                transaction.update(ingredientRef, { stock_quantity: updated });
              });
              await getDoc(ingredientRef);
            } catch (e) {
              console.error("[subtractStock] update error:", ingredient.id, e);
            }
          }
        }
      }
    }
  };

  // Helper: build map of productId -> total quantity to deduct/restore from an order
  const buildProductTotalsFromSale = (saleObj?: ISale) => {
    const totals: Record<string, number> = {};
    if (!saleObj) return totals;
    if (saleObj.products && saleObj.products.length > 0) {
      saleObj.products.forEach((p) => {
        if (!p.id) return;
        totals[p.id] = (totals[p.id] || 0) + (p.quantityToSale || 0);
      });
    }
    if (saleObj.promotion && saleObj.promotion.length > 0) {
      saleObj.promotion.forEach((promo) => {
        // each promo has promotion.applies_to with product ids and quantity_needed
        promo.promotion.applies_to.forEach((prod: any) => {
          const qty = (prod.quantity_needed || 0) * (promo.quantity || 0);
          if (!prod.id) return;
          totals[prod.id] = (totals[prod.id] || 0) + qty;
        });
      });
    }
    return totals;
  };

  // Compute diffs between editableSale and current sale and adjust stock accordingly
  const adjustStockForEdit = async (original?: ISale, edited?: ISale) => {
    if (!original || !edited) return;

    const origTotals = buildProductTotalsFromSale(original);
    const newTotals = buildProductTotalsFromSale(edited);

    // For each product id present in either map, compute delta = new - orig
    const allIds = new Set([
      ...Object.keys(origTotals),
      ...Object.keys(newTotals),
    ]);

    for (const id of Array.from(allIds)) {
      const origQty = origTotals[id] || 0;
      const newQty = newTotals[id] || 0;
      const delta = newQty - origQty; // positive -> subtract more, negative -> restore stock

      const productRef = doc(
        db,
        BUSINESS_COLLECTION,
        businessData.uid,
        STOCK_COLLECTION,
        id
      );
      const productSnap = await getDoc(productRef);
      if (!productSnap.exists()) continue;
      const currentQty = productSnap.data().quantity || 0;

      // Apply delta: subtract if delta>0, add back if delta<0
      const updatedQty = currentQty - delta; // because delta = new - orig
      await updateDoc(productRef, { quantity: updatedQty });

      // Now adjust ingredients proportionally if product has ingredients in stock list
      const productData = list.find((p) => p.id === id);
      if (productData?.ingredients && productData.ingredients.length > 0) {
        for (const ingredient of productData.ingredients) {
          const ingredientRef = doc(
            db,
            BUSINESS_COLLECTION,
            businessData.uid,
            INGREDIENTS_COLLECTION,
            ingredient.id
          );
          const ingredientSnap = await getDoc(ingredientRef);
          if (!ingredientSnap.exists()) continue;

          // compute how much of this ingredient to adjust: ingredient.quantity_needed * delta
          const qtyChange = (ingredient.quantity_needed || 0) * delta;

          // handle unit conversions similar to subtractStockAndIngredients
          let newIngredientQty = ingredientSnap.data().stock_quantity || 0;
          const ingredientUnit = ingredient.unit;
          const storedUnit = ingredientSnap.data().unit;

          if (ingredientUnit === "ml" && storedUnit === "lt") {
            const totalML = newIngredientQty * 1000;
            const updatedML = totalML - qtyChange; // qtyChange already in ml
            newIngredientQty = updatedML / 1000;
          } else if (ingredientUnit === "g" && storedUnit === "kg") {
            const totalG = newIngredientQty * 1000;
            const updatedG = totalG - qtyChange; // qtyChange already in g
            newIngredientQty = updatedG / 1000;
          } else {
            newIngredientQty = newIngredientQty - qtyChange;
          }

          if (typeof newIngredientQty === "number" && newIngredientQty < 0)
            newIngredientQty = 0;

          try {
            await runTransaction(db, async (transaction) => {
              const ingSnap = await transaction.get(ingredientRef);
              if (!ingSnap.exists()) throw new Error("Ingredient doc missing");
              // apply delta-based change: qtyChange might be positive or negative
              const current = ingSnap.data().stock_quantity || 0;
              const updated = Math.max(current - qtyChange, 0);
              transaction.update(ingredientRef, { stock_quantity: updated });
            });
            await getDoc(ingredientRef);
          } catch (e) {
            console.error(
              "[adjustStockForEdit] update error:",
              ingredient.id,
              e
            );
          }
        }
      }
    }
  };

  const isKitchenRequired = () => {
    // Verifica productos individuales
    const hasKitchenProduct = sale.products?.some((p) => p.type === "kitchen");
    // Verifica productos de promociones
    const hasKitchenPromoProduct = sale.promotion?.some((promo) =>
      promo.promotion.applies_to?.some((prod) => prod.type === "kitchen")
    );
    return Boolean(hasKitchenProduct || hasKitchenPromoProduct);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const salesCollectionRef = collection(
        db,
        BUSINESS_COLLECTION,
        businessData.uid,
        SALES_COLLECTION
      );
      // const turn = await getNextDailyCount();

      // determine cash received/change if payment is cash
      const selectedPayment = payments.find(
        (p) => p.id === sale.payment_method_id
      );

      const newSale = {
        ...sale,
        createdBy: userData.uid,
        total: getTotalSale(),
        // status: isKitchenRequired() ? "pending" : "preparing",
        status: "preparing",
        createdAt: new Date(),
        updatedAt: new Date(),
        requireKitchen: isKitchenRequired(),
        promotion: sale.promotion || [],
        // shiftID: shiftInfo?.id,
      };

      if (editableSale && (editableSale as any).id) {
        // Editing existing sale: compute diffs and adjust stock accordingly, then update sale
        await adjustStockForEdit(editableSale as ISale, newSale);
        const saleDocRef = doc(
          db,
          BUSINESS_COLLECTION,
          businessData.uid,
          SALES_COLLECTION,
          (editableSale as any).id
        );

        await updateDoc(saleDocRef, { ...newSale, updatedAt: new Date() });
      } else {
        // New sale: add and subtract stock
        await addDoc(salesCollectionRef, newSale);
        if (
          (sale.promotion && sale.promotion.length > 0) ||
          (sale.products && sale.products.length > 0)
        ) {
          await subtractStockAndIngredients();
        }
      }
      setLoading(false);
      setShowCombos(false);
      setItem({} as IProductToSale);
      setSale({} as ISale);
      // handleClose();
      showAlert("Orden creada exitosamente", "success");
    } catch (error) {
      setLoading(false);
      console.log(error);
      showAlert("Ocurrió un error al crear la orden", "error");
    }
  };

  useEffect(() => {
    if (businessData.uid) {
      getPayments();
      getList();
    }
  }, [businessData]);

  // useEffect(() => {
  //   const product = list.find(({ name }: IProduct) => name === item.name);
  //   const numbers = Array.from(
  //     Array(Number(product?.quantity) || 1).fill(0),
  //     (_, i) => i + 1
  //   );
  //   setQuantity(numbers);
  // }, [item.name, list]);

  useEffect(() => {
    if (item.name) handleAddItemsToSale();
  }, [item]);

  useEffect(() => {
    if (editableSale) {
      setSale(editableSale);
    }
  }, [editableSale]);

  // Crear cantidad de combos disponibles basado en stock de productos
  // (kept simple: if combos are shown, combos are loaded; quantity limits are handled on the promotion list level)

  useEffect(() => {
    if (showCombos && !combos.length) getCombos();
  }, [showCombos]);

  return (
    <>
      <Grid container size={{ xs: 12, md: 6 }} sx={{ p: 3 }} spacing={2}>
        {/* NUMERO DE ORDEN */}
        <Grid size={12}>
          <TextField
            name="turn"
            label="Numero de orden"
            fullWidth
            size="small"
            value={sale.turn || ""}
            inputRef={refCampo1}
            onKeyDown={(e) => handleKeyDown(e, refCampo2)}
            onChange={({ target: { name, value } }) =>
              setSale({ ...sale, [name]: value })
            }
          />
        </Grid>

        {/* SELECCION DE PRODUCTOS */}
        <Grid size={12}>
          <FormControl fullWidth size="small">
            <Autocomplete
              multiple
              value={sale.products || []}
              options={list}
              getOptionLabel={(option: any) => option.name}
              size="small"
              onChange={(_: any, newValue: any, reason: string) => {
                // maintain previous quantities for products when re-selecting
                const updatedProducts = newValue.map((item: IProduct) => ({
                  ...item,
                  quantityToSale: productQuantities[item.id] || 1,
                }));
                // update productQuantities map with any new items defaulted
                const newProductQuantities = { ...productQuantities };
                updatedProducts.forEach((p: any) => {
                  if (!newProductQuantities[p.id])
                    newProductQuantities[p.id] = p.quantityToSale;
                });
                setProductQuantities(newProductQuantities);
                setSale({ ...sale, products: updatedProducts });
              }}
              renderInput={(params) => (
                <TextField {...params} inputRef={refCampo2} label="Producto" />
              )}
            />
          </FormControl>
        </Grid>

        {sale?.products?.length > 0 && (
          <Box
            sx={{
              width: "100%",
              // maxHeight: "400px", // Limita la altura de la lista
              // overflowY: "auto", // Permite scroll vertical dentro de este Box
              // Otras propiedades para el layout interno (ej: 'display: flex', etc.)
            }}
          >
            <Grid size={{ xs: 12 }}>
              <Divider />
              <Typography py={2}>Productos</Typography>
              <Paper sx={{ p: 1 }}>
                {sale.products.map(
                  (
                    { name, price, quantityToSale, id }: IProductToSale,
                    index: number
                  ) => {
                    const variableKey = (
                      sale?.products?.[index] as IProductToSale
                    )?.isPriceVariable;
                    return (
                      <Grid
                        container
                        key={id || index}
                        alignItems="center"
                        spacing={1}
                        // sx={{ py: 1 }}
                      >
                        <Grid size={6}>
                          <Typography>{name}</Typography>
                        </Grid>
                        <Grid size={3}>
                          <TextField
                            fullWidth
                            type="number"
                            size="small"
                            inputProps={{ min: 1 }}
                            value={quantityToSale}
                            onChange={({ target: { value } }) => {
                              const qty = Math.max(Number(value) || 1, 1);
                              const updated = sale.products.map((p, i) =>
                                i === index ? { ...p, quantityToSale: qty } : p
                              );
                              // persist quantity in map
                              const prodId = sale.products[index]?.id;
                              if (prodId)
                                setProductQuantities({
                                  ...productQuantities,
                                  [prodId]: qty,
                                });
                              setSale({ ...sale, products: updated });
                            }}
                          />
                        </Grid>
                        <Grid size={3}>
                          {variableKey ? (
                            <div>
                              <TextField
                                size="small"
                                fullWidth
                                type="number"
                                value={Number(price)}
                                onChange={({ target: { value } }) => {
                                  const unitPrice = Number(value) || 0;
                                  const updated = sale.products.map((p, i) =>
                                    i === index ? { ...p, price: unitPrice } : p
                                  );
                                  setSale({ ...sale, products: updated });
                                }}
                              />
                            </div>
                          ) : (
                            <Typography>
                              {convertCurrencyFormat(
                                price * (quantityToSale || 1)
                              )}
                            </Typography>
                          )}
                        </Grid>
                        {/* <Grid size={1}>
                          <Tooltip title="Eliminar">
                            <IconButton
                              color="error"
                              onClick={() => removeItemToSale(name, "product")}
                            >
                              <span className="material-symbols-rounded">
                                delete
                              </span>
                            </IconButton>
                          </Tooltip>
                        </Grid> */}
                      </Grid>
                    );
                  }
                )}
              </Paper>
            </Grid>
            <Divider />
          </Box>
        )}
      </Grid>
      <Grid container size={{ xs: 12, md: 6 }} sx={{ p: 3 }} spacing={2}>
        {/* PROMOS  */}
        <Grid size={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={showCombos}
                onClick={() => setShowCombos((prevState) => !prevState)}
              />
            }
            label="Mostrar promociones y combos"
          />
        </Grid>

        {showCombos && combos.length > 0 && (
          <>
            <Grid size={12}>
              <FormControl fullWidth size="small">
                <Autocomplete
                  multiple
                  value={
                    sale.promotion
                      ? sale.promotion.map((p: any) => p.promotion)
                      : []
                  }
                  options={combos}
                  getOptionLabel={(option: any) => option.name || ""}
                  size="small"
                  onChange={(_: any, newValue: any[], reason: string) => {
                    if (reason === "clear") {
                      setSale({ ...sale, promotion: [] });
                      return;
                    }
                    // map selected promos using persisted quantities when available
                    const promotionsWithQty = newValue.map((promo) => ({
                      promotion: promo,
                      quantity: promoQuantities[promo.id] || 1,
                    }));
                    // update promoQuantities map with defaults
                    const newPromoQuantities = { ...promoQuantities };
                    promotionsWithQty.forEach((p) => {
                      if (!newPromoQuantities[p.promotion.id])
                        newPromoQuantities[p.promotion.id] = p.quantity;
                    });
                    setPromoQuantities(newPromoQuantities);
                    setSale({ ...sale, promotion: promotionsWithQty });
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Promoción" />
                  )}
                />
              </FormControl>
            </Grid>

            {sale.promotion && sale.promotion.length > 0 && (
              <Box
                sx={{
                  maxHeight: "400px", // Limita la altura de la lista
                  overflowY: "auto", // Permite scroll vertical dentro de este Box
                  // Otras propiedades para el layout interno (ej: 'display: flex', etc.)
                }}
              >
                <Grid size={{ xs: 12 }}>
                  <Divider />
                  <Typography py={1}>Promociones</Typography>
                  <Paper sx={{ p: 1 }}>
                    {sale.promotion.map(
                      ({ promotion, quantity }: any, index: number) => (
                        <Grid
                          container
                          key={promotion.id || index}
                          alignItems="center"
                          spacing={1}
                          sx={{ py: 1 }}
                        >
                          <Grid size={6}>
                            <Typography>{promotion.name}</Typography>
                          </Grid>
                          <Grid size={3}>
                            <TextField
                              type="number"
                              size="small"
                              value={quantity}
                              onChange={({ target: { value } }) => {
                                const qty = Math.max(Number(value) || 1, 1);
                                const updated =
                                  sale.promotion &&
                                  sale.promotion.map((p: any, i: number) =>
                                    i === index ? { ...p, quantity: qty } : p
                                  );
                                // persist quantity
                                const promoId =
                                  sale?.promotion?.[index]?.promotion?.id;
                                if (promoId) {
                                  setPromoQuantities({
                                    ...promoQuantities,
                                    [promoId]: qty,
                                  });
                                }
                                setSale({ ...sale, promotion: updated });
                              }}
                            />
                          </Grid>
                          <Grid size={2}>
                            <Typography>
                              {convertCurrencyFormat(
                                promotion.value * (quantity || 1)
                              )}
                            </Typography>
                          </Grid>
                          <Grid size={1}>
                            <Tooltip title="Eliminar">
                              <IconButton
                                color="error"
                                onClick={() =>
                                  removeItemToSale(promotion.name, "combo")
                                }
                              >
                                <span className="material-symbols-rounded">
                                  delete
                                </span>
                              </IconButton>
                            </Tooltip>
                          </Grid>
                        </Grid>
                      )
                    )}
                  </Paper>
                </Grid>
              </Box>
            )}
          </>
        )}

        <Grid size={12}>
          <FormControl component="fieldset" fullWidth>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Metodo de pago
            </Typography>
            <div>
              {payments.map((pm) => {
                const checked = sale.payment_method_id === pm.id;
                return (
                  <FormControlLabel
                    key={pm.id}
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={(_, isChecked) => {
                          setSale({
                            ...sale,
                            payment_method_id: isChecked ? pm.id : "",
                            payment_method_name: isChecked ? pm.name : "",
                          });
                        }}
                      />
                    }
                    label={pm.name}
                  />
                );
              })}
            </div>
          </FormControl>
        </Grid>
        {/* Cash received / change input */}
        {(() => {
          const selected = payments.find(
            (p) => p.id === sale.payment_method_id
          );
          const isCash =
            selected &&
            (selected.id === "cash" ||
              (selected.name || "").toLowerCase().includes("efect"));
          if (!isCash) return null;
          const total = getTotalSale();
          const receivedNum = Number(receivedCash) || 0;
          const change = Math.max(receivedNum - total, 0);
          return (
            <Grid size={12} sx={{ mt: 1 }}>
              <TextField
                label="Efectivo recibido"
                type="number"
                size="small"
                fullWidth
                value={receivedCash}
                onChange={({ target: { value } }) => setReceivedCash(value)}
              />
              <Typography sx={{ mt: 1 }}>
                Vuelto: {convertCurrencyFormat(change)}
              </Typography>
            </Grid>
          );
        })()}
        <Grid size={12}>
          <TextField
            name="observation"
            label="Observación"
            multiline
            rows={2}
            size="small"
            fullWidth
            value={sale.observation || ""}
            onChange={({ target: { name, value } }) =>
              setSale({ ...sale, [name]: value })
            }
          />
        </Grid>
      </Grid>

      <Grid container size={12} sx={{ p: 3 }}>
        <Divider />
        <Grid size={{ xs: 12 }} display="flex" justifyContent="end">
          <Typography fontSize={21}>
            Total: {convertCurrencyFormat(getTotalSale())}
          </Typography>
        </Grid>

        <Grid
          container
          display="flex"
          justifyContent="end"
          alignItems="center"
          size={{ xs: 12 }}
          sx={{ mt: 3 }}
          spacing={1}
        >
          <Button
            color="secondary"
            variant="outlined"
            size="small"
            onClick={handleClear}
          >
            Cancelar
          </Button>
          <LoadingButton
            loading={loading}
            disabled={
              loading ||
              (!(sale?.promotion && sale.promotion.length > 0) &&
                !sale?.products?.length) ||
              !sale.payment_method_id
            }
            variant="contained"
            size="small"
            color="secondary"
            onClick={handleSubmit}
          >
            Crear
          </LoadingButton>
        </Grid>
      </Grid>
    </>
  );
};

export default NewOrderModal;

// const getNextDailyCount = async () => {
//   const today = new Date();
//   const dateString = today.toISOString().split("T")[0]; //YYYY-MM-DD
//   const counterRef = doc(
//     db,
//     BUSINESS_COLLECTION,
//     businessData.uid,
//     DAILY_COUNT_COLLECTION,
//     dateString
//   );

//   try {
//     const newOrderNumber = await runTransaction(db, async (transaction) => {
//       const counterDoc = await transaction.get(counterRef);

//       let currentNumber = 0;
//       if (counterDoc.exists()) {
//         currentNumber = counterDoc.data().lastNumber + 1;
//       } else {
//         currentNumber = 1;
//       }

//       transaction.set(counterRef, { lastNumber: currentNumber });

//       return currentNumber;
//     });
//     return newOrderNumber;
//   } catch (e) {
//     console.error(
//       "Error al obtener o actualizar el número de pedido diario para",
//       businessData.uid,
//       ":",
//       e
//     );
//     throw new Error("No se pudo asignar un número de pedido.");
//   }
// };
